import { z } from 'zod';
import { ApiImageSchema, ApiImageSchemaNoId } from './Base';

export const ApartmentSchema = z.object({
    id: z.number(),
    project_name: z.string().nullable(),
    legal_name: z.string().nullable(),
    address: z.string().nullable(),
    type: z
        .union([
            z.literal('apartment'),
            z.literal('detached_house'),
            z.literal('semi_detached_house'),
            z.literal('room'),
        ])
        .nullable(),
    area: z.number().nullable(),
    living_area: z.number().nullable(),
    floor: z.number().nullable(),
    rooms: z.number().nullable(),
    parking_spot: z.string().nullable(),
    tenants: z.array(
        z.object({
            id: z.number(),
            tenant_role: z.union([
                z.literal('owner'),
                z.literal('livesWith'),
                z.literal('renting'),
                z.literal('other'),
            ]),
            fname: z.string(),
            lname: z.string().nullable(),
            phone: z.string(),
            email: z.string().nullable(),
            status: z.union([z.literal('active'), z.literal('notLoggedIn')]),
            avatar: ApiImageSchemaNoId.nullable(),
        }),
    ),
    picture: ApiImageSchema.nullable(),
    apartment_groups: z.array(z.object({ id: z.string(), name: z.string() })),
    norwegian_postal_address: z
        .object({
            street_name: z.string().nullable(),
            house_number: z.string().nullable(),
            house_letter: z.string().nullable(),
            unit_number: z.string().nullable(),
            postal_code: z.string().nullable(),
            postal_area: z.string().nullable(),
            section_number: z.string().nullable(),
            section_number_alias: z.string().nullable(),
            share_number: z.string().nullable(),
            share_number_alias: z.string().nullable(),
        })
        .nullable(),
});

export type Apartment = z.infer<typeof ApartmentSchema>;

export type TenantRole = Apartment['tenants'][0]['tenant_role'];

export const buildAddress = (postalAddress: Apartment['norwegian_postal_address']) => {
    if (!postalAddress) {
        return '';
    }
    let info = '';
    if (postalAddress.street_name) {
        info += postalAddress.street_name;
    }
    if (postalAddress.house_number) {
        info += ` ${postalAddress.house_number}`;
        info = info.trim();
        if (postalAddress.house_letter) {
            info += `${postalAddress.house_letter}`;
        }
    } else if (postalAddress.house_letter) {
        info += ` ${postalAddress.house_letter}`;
        info = info.trim();
    }
    if (postalAddress.unit_number) {
        info += ` ${postalAddress.unit_number}`;
        info = info.trim();
    }

    if ((postalAddress.postal_code || postalAddress.postal_area) && info) {
        info += ',\n';
        const strings = [];
        if (postalAddress.postal_code) {
            strings.push(postalAddress.postal_code);
        }
        if (postalAddress.postal_area) {
            strings.push(postalAddress.postal_area);
        }
        info += strings.join(' ');
    }

    return info;
};

export const buildLegalName = (
    postal_address: Apartment['norwegian_postal_address'],
    property: 'share_number' | 'section_number',
) => {
    if (!postal_address || !postal_address[property]) {
        return '';
    }
    const alias = property === 'share_number' ? 'share_number_alias' : 'section_number_alias';
    if (postal_address[alias]) {
        return `${postal_address[alias]}${postal_address[property]}`;
    }
    return postal_address[property];
};
